import { useState, useEffect, useCallback } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { CircleNotch, Warning, TrendUp, ChartLine, DeviceMobile, Desktop, EyeSlash, FileXls, FilePdf, WhatsappLogo, ArrowsClockwise, Users } from '@phosphor-icons/react';

const fmtQ = (n) => n == null ? '—' : `Q ${Number(n).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Card = ({ title, icon: Icon, children, testid }) => (
  <div data-testid={testid} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
    <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
      <Icon size={18} className="text-blue-600" /> {title}
    </h3>
    {children}
  </div>
);

const PRIORIDAD_STYLE = {
  ALTA: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  MEDIA: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  BAJA: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
  'SIN VENTAS': 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
};

const StockBajoCard = ({ empresa }) => {
  const [umbral, setUmbral] = useState(5);
  const [orden, setOrden] = useState('prioridad');
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);

  const fetchRep = useCallback(async (u, o) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tienda/${empresa}/admin/reportes/stock-bajo`, { params: { umbral: u, orden: o } });
      setRep(data);
      if (data.sync_en_curso) setSyncing(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando stock bajo');
    } finally {
      setLoading(false);
    }
  }, [empresa]);

  useEffect(() => { fetchRep(umbral, orden); }, [umbral, orden, fetchRep]);

  const syncVentas = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/tienda/${empresa}/admin/reportes/sync-ventas`);
      toast.info(data.detail || 'Sincronizando ventas...');
      setTimeout(() => { setSyncing(false); fetchRep(umbral, orden); }, 60000);
    } catch (e) {
      setSyncing(false);
      toast.error(e?.response?.data?.detail || 'Error iniciando sincronización');
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/tienda/${empresa}/admin/reportes/stock-bajo/export`, {
        params: { umbral, orden }, responseType: 'blob',
      });
      const u = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = u;
      a.download = `stock-bajo-${umbral}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
      toast.success('Excel descargado');
    } catch {
      toast.error('Error exportando Excel');
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await api.get(`/tienda/${empresa}/admin/reportes/stock-bajo/export-pdf`, {
        params: { umbral, orden }, responseType: 'blob',
      });
      const u = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = u;
      a.download = `stock-bajo-${umbral}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
      toast.success('PDF descargado');
    } catch {
      toast.error('Error exportando PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const sendWhatsapp = async () => {
    setSendingWa(true);
    try {
      const { data } = await api.post(`/tienda/${empresa}/admin/reportes/stock-bajo/whatsapp`, { umbral, orden });
      if (data.enviado) {
        toast.success(`PDF enviado por WhatsApp a ${data.destino} (${data.metodo})`);
      } else {
        toast.error('No se pudo enviar por WhatsApp — revisa la ventana de 24h o las líneas configuradas');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando por WhatsApp');
    } finally {
      setSendingWa(false);
    }
  };

  const meta = rep?.ventas_meta;

  return (
    <Card title="Stock bajo + prioridad de compra" icon={Warning} testid="reporte-stock-bajo">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-slate-500 mr-1">Existencia menor a:</span>
        {[5, 10, 15, 20, 30].map((u) => (
          <button key={u} data-testid={`umbral-${u}`} onClick={() => setUmbral(u)}
            className={`h-8 px-3 text-xs font-bold rounded-md transition-colors ${umbral === u ? 'bg-blue-600 text-white' : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            {u}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          <button data-testid="stock-export-excel" onClick={exportExcel} disabled={exporting}
            className="h-8 px-3 text-xs font-bold rounded-md border border-emerald-400 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-1 disabled:opacity-50">
            {exporting ? <CircleNotch size={13} className="animate-spin" /> : <FileXls size={13} />} Excel
          </button>
          <button data-testid="stock-export-pdf" onClick={exportPdf} disabled={exportingPdf}
            className="h-8 px-3 text-xs font-bold rounded-md border border-red-400 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-1 disabled:opacity-50">
            {exportingPdf ? <CircleNotch size={13} className="animate-spin" /> : <FilePdf size={13} />} PDF
          </button>
          <button data-testid="stock-send-whatsapp" onClick={sendWhatsapp} disabled={sendingWa}
            className="h-8 px-3 text-xs font-bold rounded-md border border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 flex items-center gap-1 disabled:opacity-50">
            {sendingWa ? <CircleNotch size={13} className="animate-spin" /> : <WhatsappLogo size={13} weight="fill" />} WhatsApp
          </button>
          <button data-testid="stock-sync-ventas" onClick={syncVentas} disabled={syncing}
            className="h-8 px-3 text-xs font-bold rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1 disabled:opacity-50">
            <ArrowsClockwise size={13} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Sincronizando…' : 'Actualizar ventas'}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500">Ordenar por:</span>
        {[['prioridad', 'Prioridad de compra'], ['existencia', 'Existencia']].map(([k, l]) => (
          <button key={k} data-testid={`orden-${k}`} onClick={() => setOrden(k)}
            className={`h-7 px-2.5 text-[11px] font-bold rounded-md transition-colors ${orden === k ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 'border border-slate-300 dark:border-slate-600 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            {l}
          </button>
        ))}
        <span className="text-[10px] text-slate-400 ml-auto" data-testid="ventas-meta">
          {meta ? `Ventas: ${meta.desde} → ${meta.hasta} (${meta.ventas} facturas)` : 'Ventas sin sincronizar — presiona "Actualizar ventas"'}
        </span>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><CircleNotch size={22} className="animate-spin text-blue-600" /></div>
      ) : !rep ? null : (
        <>
          <div className="flex gap-4 text-xs">
            <span className="font-bold text-slate-700 dark:text-slate-200" data-testid="stock-bajo-total">{rep.total} SKUs con menos de {rep.umbral}</span>
            <span className="font-bold text-red-600" data-testid="stock-bajo-agotados">{rep.agotados} agotados</span>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white dark:bg-slate-900">
                <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <th className="py-1.5 pr-2">Producto</th>
                  <th className="py-1.5 pr-2 text-right">Exist.</th>
                  <th className="py-1.5 pr-2 text-right">Margen</th>
                  <th className="py-1.5 pr-2 text-right">Vendidos</th>
                  <th className="py-1.5 pr-2 text-right">Ganancia</th>
                  <th className="py-1.5 text-center">Prioridad</th>
                </tr>
              </thead>
              <tbody>
                {rep.data.map((p) => (
                  <tr key={p.sku} className="border-b border-slate-100 dark:border-slate-800" data-testid={`stock-row-${p.sku}`}>
                    <td className="py-1.5 pr-2">
                      <p className="font-semibold text-slate-800 dark:text-slate-100 leading-tight">
                        {p.descripcion} {p.hidden && <EyeSlash size={12} className="inline text-slate-400" title="Oculto en tienda" />}
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono">{p.sku} · {p.categoria}</p>
                    </td>
                    <td className={`py-1.5 pr-2 text-right font-black ${p.agotado ? 'text-red-600' : 'text-amber-600'}`}>
                      {p.agotado ? 'AGOT.' : p.existencia}
                    </td>
                    <td className="py-1.5 pr-2 text-right font-semibold">{p.margen_pct == null ? '—' : `${p.margen_pct}%`}</td>
                    <td className="py-1.5 pr-2 text-right">{p.unidades_vendidas || '—'}</td>
                    <td className={`py-1.5 pr-2 text-right font-bold ${p.ganancia_periodo > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {p.ganancia_periodo ? fmtQ(p.ganancia_periodo) : '—'}
                    </td>
                    <td className="py-1.5 text-center">
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${PRIORIDAD_STYLE[p.prioridad] || ''}`}>{p.prioridad}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rep.data.length === 0 && <p className="text-center py-6 text-slate-400 text-sm">Ningún producto por debajo del umbral.</p>}
          </div>
        </>
      )}
    </Card>
  );
};

const MargenCard = ({ empresa }) => {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tienda/${empresa}/admin/reportes/margen-familias`);
        setRep(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Error cargando margen por familia');
      } finally {
        setLoading(false);
      }
    })();
  }, [empresa]);

  return (
    <Card title="Rentabilidad por familia (costo HarpERP vs precio tienda)" icon={TrendUp} testid="reporte-margen-familias">
      {loading ? (
        <div className="flex justify-center py-8"><CircleNotch size={22} className="animate-spin text-blue-600" /></div>
      ) : !rep ? null : (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white dark:bg-slate-900">
              <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <th className="py-1.5 pr-2">Familia</th><th className="py-1.5 pr-2 text-right">Productos</th>
                <th className="py-1.5 pr-2 text-right">Margen %</th><th className="py-1.5 pr-2 text-right">Ganancia potencial</th>
                <th className="py-1.5 pr-2 text-right">Valor inventario</th><th className="py-1.5 text-right">Bajo costo</th>
              </tr>
            </thead>
            <tbody>
              {rep.data.map((f, i) => (
                <tr key={f.familia} className="border-b border-slate-100 dark:border-slate-800" data-testid={`margen-row-${i}`}>
                  <td className="py-1.5 pr-2 font-semibold text-slate-800 dark:text-slate-100">
                    {i === 0 && f.margen_pct != null && <span className="text-[9px] bg-emerald-100 text-emerald-700 rounded px-1 mr-1 font-black">TOP</span>}
                    {f.familia}
                  </td>
                  <td className="py-1.5 pr-2 text-right text-slate-500">{f.productos}</td>
                  <td className={`py-1.5 pr-2 text-right font-black ${f.margen_pct == null ? 'text-slate-400' : f.margen_pct >= 40 ? 'text-emerald-600' : f.margen_pct >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                    {f.margen_pct == null ? '—' : `${f.margen_pct}%`}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-semibold">{fmtQ(f.margen_q_potencial)}</td>
                  <td className="py-1.5 pr-2 text-right text-slate-500">{fmtQ(f.valor_inventario)}</td>
                  <td className={`py-1.5 text-right font-bold ${f.bajo_costo > 0 ? 'text-red-600' : 'text-slate-400'}`}>{f.bajo_costo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const TraficoCard = ({ empresa }) => {
  const [days, setDays] = useState(30);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/tienda/${empresa}/admin/reportes/trafico`, { params: { days } });
        setRep(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Error cargando tráfico');
      } finally {
        setLoading(false);
      }
    })();
  }, [empresa, days]);

  const maxDia = Math.max(1, ...(rep?.por_dia || []).map((d) => d.visitas));

  return (
    <Card title="Tráfico de la tienda" icon={ChartLine} testid="reporte-trafico">
      <div className="flex items-center gap-1.5">
        {[7, 30, 90].map((d) => (
          <button key={d} data-testid={`trafico-days-${d}`} onClick={() => setDays(d)}
            className={`h-8 px-3 text-xs font-bold rounded-md transition-colors ${days === d ? 'bg-blue-600 text-white' : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            {d} días
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><CircleNotch size={22} className="animate-spin text-blue-600" /></div>
      ) : !rep ? null : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="p-3 rounded-md bg-slate-50 dark:bg-slate-800">
              <p className="text-[10px] uppercase text-slate-400 font-bold">Visitas</p>
              <p className="text-xl font-black text-slate-900 dark:text-white" data-testid="trafico-visitas-total">{rep.visitas_total}</p>
            </div>
            <div className="p-3 rounded-md bg-slate-50 dark:bg-slate-800">
              <p className="text-[10px] uppercase text-slate-400 font-bold">Productos vistos</p>
              <p className="text-xl font-black text-slate-900 dark:text-white">{rep.vistas_producto_total}</p>
            </div>
            <div className="p-3 rounded-md bg-slate-50 dark:bg-slate-800">
              <p className="text-[10px] uppercase text-slate-400 font-bold flex items-center gap-1"><DeviceMobile size={12} /> Móvil</p>
              <p className="text-xl font-black text-slate-900 dark:text-white">{rep.devices?.movil || 0}</p>
            </div>
            <div className="p-3 rounded-md bg-slate-50 dark:bg-slate-800">
              <p className="text-[10px] uppercase text-slate-400 font-bold flex items-center gap-1"><Desktop size={12} /> Escritorio</p>
              <p className="text-xl font-black text-slate-900 dark:text-white">{rep.devices?.escritorio || 0}</p>
            </div>
          </div>
          {rep.visitas_total === 0 ? (
            <p className="text-xs text-slate-400 italic">Aún no hay visitas registradas. El contador empieza a funcionar desde que se activó esta función (haz deploy para contar visitas en producción).</p>
          ) : (
            <div className="space-y-1">
              {rep.por_dia.slice(-14).map((d) => (
                <div key={d.fecha} className="flex items-center gap-2 text-xs">
                  <span className="w-20 text-slate-500 font-mono">{d.fecha.slice(5)}</span>
                  <div className="flex-1 h-4 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
                    <div className="h-full bg-blue-500 rounded" style={{ width: `${(d.visitas / maxDia) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right font-bold">{d.visitas}</span>
                </div>
              ))}
            </div>
          )}
          {rep.top_productos.length > 0 && (
            <div>
              <p className="text-[10px] uppercase text-slate-400 font-bold mb-1.5">Productos más vistos</p>
              <ul className="space-y-1 text-xs">
                {rep.top_productos.map((p, i) => (
                  <li key={p.sku} className="flex justify-between gap-2" data-testid={`top-producto-${p.sku}`}>
                    <span className="truncate text-slate-700 dark:text-slate-200"><span className="text-slate-400 font-mono mr-1">{i + 1}.</span>{p.descripcion}</span>
                    <span className="font-black text-blue-600 whitespace-nowrap">{p.vistas} vistas</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

const ClientesCard = ({ empresa }) => {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [promoOpen, setPromoOpen] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [sendingPromo, setSendingPromo] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tienda/${empresa}/admin/reportes/clientes`);
        setRep(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Error cargando clientes');
      } finally {
        setLoading(false);
      }
    })();
  }, [empresa]);

  const toggle = (tel) => setSelected((s) => (s.includes(tel) ? s.filter((t) => t !== tel) : [...s, tel]));
  const allTels = (rep?.data || []).map((c) => c.telefono).filter(Boolean);
  const toggleAll = () => setSelected(selected.length === allTels.length ? [] : allTels);

  const sendPromo = async () => {
    if (!titulo.trim() || !mensaje.trim()) {
      toast.error('Escribe el título y el mensaje de la promo');
      return;
    }
    setSendingPromo(true);
    try {
      const { data } = await api.post(`/tienda/${empresa}/admin/reportes/clientes/promo`, {
        telefonos: selected, titulo, mensaje,
      });
      toast.success(`Promo enviada: ${data.enviados} entregadas${data.fallidos ? `, ${data.fallidos} fallidas` : ''}`);
      setPromoOpen(false);
      setSelected([]);
      setTitulo('');
      setMensaje('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando promo');
    } finally {
      setSendingPromo(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/tienda/${empresa}/admin/reportes/clientes/export`, { responseType: 'blob' });
      const u = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = u;
      a.download = 'clientes-tienda.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
      toast.success('Excel descargado');
    } catch {
      toast.error('Error exportando Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card title="Clientes de la tienda (para campañas)" icon={Users} testid="reporte-clientes-tienda">
      {loading ? (
        <div className="flex justify-center py-8"><CircleNotch size={22} className="animate-spin text-blue-600" /></div>
      ) : !rep ? null : (
        <>
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <span className="font-bold text-slate-700 dark:text-slate-200" data-testid="clientes-total">{rep.total_clientes} clientes</span>
            <span className="font-bold text-blue-600">{rep.con_email} con correo</span>
            <div className="ml-auto flex items-center gap-1.5">
              <button data-testid="clientes-promo-btn" onClick={() => setPromoOpen(!promoOpen)} disabled={selected.length === 0}
                className="h-8 px-3 text-xs font-bold rounded-md border border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 flex items-center gap-1 disabled:opacity-40">
                <WhatsappLogo size={13} weight="fill" /> Enviar promo ({selected.length})
              </button>
              <button data-testid="clientes-export-excel" onClick={exportExcel} disabled={exporting}
                className="h-8 px-3 text-xs font-bold rounded-md border border-emerald-400 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-1 disabled:opacity-50">
                {exporting ? <CircleNotch size={13} className="animate-spin" /> : <FileXls size={13} />} Excel
              </button>
            </div>
          </div>
          {promoOpen && selected.length > 0 && (
            <div className="border border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-md p-3 space-y-2" data-testid="promo-form">
              <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300">Promo por WhatsApp a {selected.length} cliente(s) — se envía como plantilla aprobada (llega aunque no hayan escrito recientemente)</p>
              <input data-testid="promo-titulo-input" placeholder="Título (ej: Promo de la semana)" value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={150}
                className="w-full h-9 px-3 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900" />
              <textarea data-testid="promo-mensaje-input" placeholder="Mensaje (ej: 15% de descuento en chapas esta semana. Pide en nuestra tienda: enlace...)" rows={3} value={mensaje} onChange={(e) => setMensaje(e.target.value)} maxLength={600}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 resize-none" />
              <div className="flex gap-2">
                <button data-testid="promo-send-btn" onClick={sendPromo} disabled={sendingPromo}
                  className="h-9 px-4 text-xs font-bold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5 disabled:opacity-50">
                  {sendingPromo ? <CircleNotch size={13} className="animate-spin" /> : <WhatsappLogo size={13} weight="fill" />}
                  {sendingPromo ? 'Enviando…' : `Enviar a ${selected.length}`}
                </button>
                <button onClick={() => setPromoOpen(false)} className="h-9 px-3 text-xs font-semibold rounded-md border border-slate-300 dark:border-slate-600 text-slate-500">Cancelar</button>
              </div>
            </div>
          )}
          {rep.data.length === 0 ? (
            <p className="text-center py-6 text-slate-400 text-sm">Aún no hay pedidos en la tienda.</p>
          ) : (
            <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white dark:bg-slate-900">
                  <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-1.5 pr-2">
                      <input type="checkbox" data-testid="clientes-select-all" checked={selected.length === allTels.length && allTels.length > 0} onChange={toggleAll} className="cursor-pointer" />
                    </th>
                    <th className="py-1.5 pr-2">Cliente</th><th className="py-1.5 pr-2">Teléfono</th>
                    <th className="py-1.5 pr-2">Correo</th><th className="py-1.5 pr-2 text-right">Pedidos</th>
                    <th className="py-1.5 pr-2 text-right">Total</th><th className="py-1.5 text-right">Último</th>
                  </tr>
                </thead>
                <tbody>
                  {rep.data.map((c, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-800" data-testid={`cliente-row-${i}`}>
                      <td className="py-1.5 pr-2">
                        {c.telefono && <input type="checkbox" data-testid={`cliente-check-${i}`} checked={selected.includes(c.telefono)} onChange={() => toggle(c.telefono)} className="cursor-pointer" />}
                      </td>
                      <td className="py-1.5 pr-2 font-semibold text-slate-800 dark:text-slate-100">{c.nombre || '—'}</td>
                      <td className="py-1.5 pr-2 font-mono text-slate-500">{c.telefono || '—'}</td>
                      <td className="py-1.5 pr-2 text-blue-600">{c.email || '—'}</td>
                      <td className="py-1.5 pr-2 text-right font-bold">{c.pedidos}</td>
                      <td className="py-1.5 pr-2 text-right font-bold">{fmtQ(c.total)}</td>
                      <td className="py-1.5 text-right text-slate-500">{c.ultimo_pedido || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export const TiendaReportesTab = ({ empresa }) => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="tienda-reportes-tab">
    <div className="xl:col-span-2"><TraficoCard empresa={empresa} /></div>
    <StockBajoCard empresa={empresa} />
    <MargenCard empresa={empresa} />
    <div className="xl:col-span-2"><ClientesCard empresa={empresa} /></div>
  </div>
);
